<script setup lang="ts">
import { onMounted, ref, onBeforeUnmount, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserProfileStore } from '@stores/useUserProfileStore.ts';
import TopNavbarDesktopAuthenticated from '@layout/nav-v2/desktop/components/TopNavbarDesktopAuthenticated.vue';
import TopNavbarDesktopAnonymous from '@layout/nav-v2/desktop/components/TopNavbarDesktopAnonymous.vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import Icon from '@components/Icon.vue';
import IconLogo from '@icons/ph_logo.svg';
import { URL_HOME } from '@tools/routes/routes';
import TopNavbarDesktopPanel from '@layout/nav-v2/desktop/components/TopNavbarDesktopPanel.vue';
import { useDataReady } from '@composables/useDataReady.ts';

const { user } = useUserProfileStore();

const navbarStore = useNavbarStore();
const { isCurrentPage } = navbarStore;
const { isTopPanelOpen } = storeToRefs(navbarStore);

const { isDataReady, setDataIsReady } = useDataReady();

const isScrolled = ref<boolean>(false);

const handleScroll = (): void => {
  isScrolled.value = window.scrollY > 0;
};

const isHomepage = computed<boolean>(() => isCurrentPage(URL_HOME));

onMounted(() => {
  document.getElementById('top-navbar-desktop-seo')?.classList.add('!hidden');

  document.getElementById('top-navbar-desktop')?.classList.remove('!hidden');

  window.addEventListener('scroll', handleScroll);

  setDataIsReady();
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
  <nav
    class="top-navbar-desktop-wrapper h-full"
    :data-ready="isDataReady"
  >
    <div
      :class="[
        'top-navbar-desktop',
        { 'top-navbar-desktop--with-border': !isHomepage },
        { 'top-navbar-desktop--scrolled': isScrolled && isHomepage },
        'w-full h-full',
        'px-4',
        'flex items-center',
      ]"
    >
      <a
        :href="URL_HOME"
        data-cy-ref="top-navbar-desktop__link--home"
      >
        <Icon
          :svg="IconLogo"
          width="75"
          height="40"
        />
      </a>

      <TopNavbarDesktopAuthenticated
        v-if="user"
        class="ml-3 grow"
      />

      <TopNavbarDesktopAnonymous
        v-else
        class="ml-9 grow"
      />
    </div>

    <TopNavbarDesktopPanel v-show="isTopPanelOpen" />
  </nav>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.top-navbar-desktop {
  &--with-border {
    box-shadow: inset 0 -1px 0 $colorGray;
  }

  &--scrolled {
    box-shadow: 0 toRem(2) toRem(4) 0 rgb(0 0 0 / 0.1);
  }
}
</style>
